import { createRef } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import type { Question } from '../../types';
import type { LayoutNode } from '../layout/types';
import {
  exploreReachableRenderedFieldQids,
  readRenderedFieldQids,
  type CoverageSearchResult,
} from './bespokeCoverageSearch';
import {
  JsonReachabilityHarness,
  type JsonReachabilityHarnessHandle,
} from './JsonReachabilityHarness';

export type LayoutCoverageResult = CoverageSearchResult;

/**
 * Runs the same reachability heuristic as `checkBespokeCoverage`, but mounts
 * `BespokeLayoutRenderer` inside a disposable DOM node via `react-dom/client`.
 */
export async function checkLayoutJsonReachability(
  layout: LayoutNode[],
  schema: Question[]
): Promise<LayoutCoverageResult> {
  if (typeof document === 'undefined') {
    return { covered: [], uncovered: [] };
  }

  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  container.style.cssText =
    'position:absolute;left:-99999px;top:0;width:min(960px,100vw);visibility:hidden';
  document.body.appendChild(container);

  const harnessRef = createRef<JsonReachabilityHarnessHandle>();
  const rootHolder: { r: Root | null } = { r: null };
  flushSync(() => {
    rootHolder.r = createRoot(container);
    rootHolder.r.render(
      <JsonReachabilityHarness
        ref={harnessRef}
        layout={layout}
        schema={schema}
      />
    );
  });

  try {
    return await exploreReachableRenderedFieldQids({
      schema,
      getRenderedQids: () => readRenderedFieldQids(container),
      async setAnswers(answers) {
        flushSync(() => harnessRef.current?.replaceAnswers(answers));
      },
    });
  } finally {
    rootHolder.r?.unmount();
    container.remove();
  }
}
